import { useState } from 'react';
import { Link } from 'react-router-dom';
import { AlertCircle } from 'lucide-react';
import { QUESTIONS, answersLine, quickAnswer, unitName, unitLabel, inr, YEARS } from '../../data/compare';

// The four quick questions, folded into one line once answered, then the
// three answer lines. The answer shows at once with the default answers.

function Questions({ answers, onChange }) {
  return (
    <div className="mt-3 grid gap-3 sm:grid-cols-2" data-testid="quick-questions">
      {QUESTIONS.map((q) => (
        <div key={q.key}>
          <p className="text-xs font-semibold text-gray-700">{q.label}</p>
          <div className="mt-1 flex flex-wrap gap-1.5">
            {q.options.map((o) => {
              const on = String(answers[q.key]) === String(o.v);
              return (
                <button key={o.v} type="button" onClick={() => onChange({ ...answers, [q.key]: o.v })}
                  data-testid={`q-${q.key}-${o.v}`}
                  className={`rounded-full border px-3 py-1.5 text-xs font-medium transition ${on ? 'border-blue-600 bg-blue-600 text-white' : 'border-gray-300 bg-white text-gray-700 hover:border-blue-400'}`}>
                  {o.label}
                </button>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
}

function Line({ label, unit, note, testId, strong }) {
  return (
    <p className="text-sm text-gray-800" data-testid={testId}>
      <span className="text-gray-600">{label}: </span>
      <span className={`font-bold ${strong ? 'text-green-700' : 'text-gray-900'}`}>{unit}{strong ? '.' : ''}</span>
      {note && <span className="text-gray-600"> {note}</span>}
    </p>
  );
}

export default function QuickAnswer({ units, answers, onChange }) {
  const [editing, setEditing] = useState(false);
  const ans = quickAnswer(units, answers);

  return (
    <section className="mt-5 rounded-xl border border-blue-100 bg-blue-50 p-4" data-testid="quick-answer">
      <h2 className="text-base font-bold text-gray-900 md:text-lg">Quick answer</h2>
      {editing ? (
        <>
          <Questions answers={answers} onChange={onChange} />
          <button type="button" onClick={() => setEditing(false)} data-testid="quick-done"
            className="mt-3 rounded-md bg-blue-600 px-4 py-2 text-xs font-semibold text-white hover:bg-blue-700">Done</button>
        </>
      ) : (
        <p className="mt-1 text-xs text-gray-700" data-testid="quick-answers-line">
          {answersLine(answers)} ·{' '}
          <button type="button" onClick={() => setEditing(true)} data-testid="quick-change" className="font-semibold text-blue-700 underline">Change</button>
        </p>
      )}

      {!ans ? (
        <p className="mt-3 text-sm text-gray-700">Add at least one AC to see the answer.</p>
      ) : (
        <div className="mt-3 space-y-1.5">
          <Line testId="ans-cheapest" label="Cheapest to buy" unit={unitLabel(ans.cheapest)} note={`at ${inr(ans.cheapest.price)}`} />
          <Line testId="ans-cheapest5" label={`Cheapest over ${YEARS} years`} unit={unitLabel(ans.cheapest5)} note={`at ${inr(ans.cheapest5.fiveYear)} (price + ${YEARS} years of electricity)`} />
          {ans.noHeat ? (
            <p className="text-sm text-gray-800" data-testid="ans-best"><span className="text-gray-600">Best for your room: </span><span className="font-bold text-red-700">{ans.noHeatText}</span></p>
          ) : ans.best && (
            <>
              <Line testId="ans-best" label="Best for your room" unit={unitLabel(ans.best.unit)} note={ans.best.reason} strong />
              {!ans.best.unit.inStock && (
                <p className="text-xs text-gray-700" data-testid="ans-stock-note">
                  {unitName(ans.best.unit)} is not in stock right now.{' '}
                  {ans.best.bestInStock ? <>The best one in stock is <b>{unitName(ans.best.bestInStock)}</b>.</> : 'We order it for you.'}
                </p>
              )}
            </>
          )}
        </div>
      )}

      {ans && ans.warnings.map((w) => (
        <div key={w.key} className="mt-3 flex gap-2 rounded-lg border border-amber-200 bg-amber-50 p-3 text-xs text-amber-900" data-testid={`warning-${w.key}`}>
          <AlertCircle size={16} className="mt-0.5 shrink-0 text-amber-600" aria-hidden="true" />
          <div>
            <p className="font-semibold">{w.text}</p>
            {w.lines && <ul className="mt-1 list-disc pl-4">{w.lines.map((l) => <li key={l}>{l}</li>)}</ul>}
            {w.link && <Link to={w.link} className="mt-1 inline-block font-semibold text-blue-700 underline">{w.linkText}</Link>}
          </div>
        </div>
      ))}
    </section>
  );
}
